import { useEffect, type ComponentProps } from "react";
import { useFormikContext } from "formik";
// @ts-expect-error pure js
import TextInput from "../UI/TextInput";
import BalanceTargetFields from "./BalanceTargetFields";
import BalanceTypeSelect from "./BalanceTypeSelect";
import FileInput from "../UI/FileInput";
// @ts-expect-error pure js
import { DocType } from "../../utils/constants/documents";
// @ts-expect-error pure js
import { currencyFormatter } from "../../api/Functions";
import BalanceSplitFields from "./BalanceSplitFields";
import { getParentIdPropertyFromDomain } from "../../utils/balances";
// @ts-expect-error pure js
import { useBalanceTypes } from "../../hooks/use-balances";
import {
  BalanceDomain,
  BalanceTypeCategory,
} from "../../utils/constants/balances";
// @ts-expect-error pure js
import Checkbox from "../UI/Checkbox";
import CheckboxStandalone from "../UI/CheckboxStandalone";
// @ts-expect-error pure js
import { isChargeOrTravauxType } from "../../utils/index";
// @ts-expect-error pure js
import { Interrogation } from "../UI/Icons.jsx";
import Tooltip from "../UI/Tooltip";
import RefacturationsFieldArray from "./RefacturationsFieldArray";
// @ts-expect-error pure js
import { ReactSelect } from "../UI/Select.jsx";
import Text from "../UI/Text";
import type { Balance } from "../../models/balance";
import styles from "./BalanceFields.module.css";
import {
  BalanceAmountDetailsFields,
  BalanceEnergyField,
} from "./BalanceDetailsFields";

// Options de taux de TVA disponibles (en pourcentage)
const TVA_RATE_OPTIONS = [
  { value: 20, label: "20%" },
  { value: 10, label: "10%" },
  { value: 5.5, label: "5,5%" },
];

const DISABLED_FIELD_HINT_TRANSAC =
  "La valeur n'est pas modifiable car associée à une transaction de votre compte bancaire.";
const DISABLED_FIELD_HINT_SPLIT =
  "La valeur n'est pas modifiable car venant d'une répartition.";

class OperationType {
  static SIMPLE = "simple";
  static SPLIT = "split";
}

interface BalanceFieldsProps {
  isFromTransac?: boolean;
  isFromSplit?: boolean;
  balanceId?: number | null;
  parentId?: number;
  domaineBalance?: string;
  prefix?: string;
  withJustificatif?: boolean;
  context?: ComponentProps<typeof BalanceTargetFields>["context"];
  isSplit?: boolean;
  isEdit?: boolean;
  childBalances?: Balance[];
  isRecuperable?: boolean;
}

export default function BalanceFields({
  isFromTransac,
  isFromSplit,
  balanceId,
  parentId,
  domaineBalance,
  prefix = "",
  withJustificatif = false,
  context,
  isSplit = false,
  isEdit = false,
  childBalances,
  isRecuperable = false,
}: BalanceFieldsProps) {
  const operationType = isSplit ? OperationType.SPLIT : OperationType.SIMPLE;
  const { values, setFieldValue } = useFormikContext<Record<string, unknown>>();
  const { balanceTypes, balanceTypesByCategory } = useBalanceTypes();

  // Récupérer l'id_bien depuis les values pour filtrer les locataires
  const propertyId = values[`${prefix}id_bien`] as number | string | undefined;

  const splitInputsName = `${prefix}balances`;

  const selectedTypeStaticId = values[`${prefix}type`];
  const selectedType = balanceTypes[selectedTypeStaticId];
  const hasPeriod = selectedType?.has_period;
  const showRemboursementCheckbox =
    selectedTypeStaticId &&
    isChargeOrTravauxType(selectedTypeStaticId, balanceTypesByCategory);

  // Un encaissement (paiement, CAF…) n'est pas soumis à TVA : la case ne
  // s'affiche que pour les types hors catégorie paiement.
  const isPaiementType =
    selectedType?.category === BalanceTypeCategory.LOCATAIRE_PAIEMENT;

  const tauxTvaFieldName = `${prefix}taux_tva`;
  const tauxTvaPct = parseFloat(String(values[tauxTvaFieldName])) || 0;
  const montant = Math.abs(parseFloat(String(values[`${prefix}montant`]))) || 0;
  const isTvaEnabled = tauxTvaPct > 0;
  const multiplicateur = 1 + tauxTvaPct / 100;
  const montantHT = isTvaEnabled ? montant / multiplicateur : montant;
  const montantTVA = montant - montantHT;

  useEffect(() => {
    const promises = [];
    if (operationType === OperationType.SIMPLE) {
      promises.push(setFieldValue(splitInputsName, undefined));
    }
    Promise.all(promises).catch((e) => console.error(e));
  }, [operationType, setFieldValue, splitInputsName]);

  useEffect(() => {
    if (isPaiementType && tauxTvaPct > 0) {
      setFieldValue(tauxTvaFieldName, 0).catch((e) => console.error(e));
    }
  }, [isPaiementType, tauxTvaPct, setFieldValue, tauxTvaFieldName]);

  const totalAmountCents = Math.round(Number(values.montant) * 100) || 0;

  // La répartition suit la cible choisie, qui n'est pas forcément l'entité de la page.
  const splitDomaine =
    (values[`${prefix}domaine`] as string | undefined) || domaineBalance;
  const splitParentId =
    Number(values[`${prefix}${getParentIdPropertyFromDomain(splitDomaine)}`]) ||
    parentId;

  let disabledFieldHint;
  if (isFromTransac) {
    disabledFieldHint = DISABLED_FIELD_HINT_TRANSAC;
  } else if (isFromSplit) {
    disabledFieldHint = DISABLED_FIELD_HINT_SPLIT;
  }

  return (
    <>
      {/* Le contexte est l'opt-in du choix d'affectation : l'édition groupée s'en passe,
      elle soumet par `batch-update`, une route par entité qui ne porte pas le déplacement.
      Un dossier non comptable reste une cible : le choisir ventile l'écriture sur ses
      lots, choisir un lot l'y affecte directement. */}
      {context && (
        <BalanceTargetFields
          prefix={prefix}
          context={context}
          isEdit={isEdit}
          disabled={isFromSplit}
          withFolders
        />
      )}
      <TextInput
        type="date"
        name={`${prefix}date_transac`}
        disabled={isFromTransac || isFromSplit}
        title={isFromTransac || isFromSplit ? disabledFieldHint : undefined}
        label={"Date de l'opération"}
        mandatory
        marginForm={""}
      />
      {!context && (
        <BalanceTypeSelect
          prefix={prefix}
          disabled={isFromSplit}
          isEdit={isEdit}
        />
      )}
      <BalanceEnergyField
        prefix={prefix}
        balanceType={selectedType}
        disabled={isFromSplit}
      />
      {hasPeriod && (
        <div className={styles.period}>
          <TextInput
            type="date"
            name={`${prefix}periode_debut`}
            label={"Période du"}
            marginForm={""}
            disabled={isFromSplit}
            mandatory
          />
          <TextInput
            type="date"
            name={`${prefix}periode_fin`}
            label={"au"}
            marginForm={""}
            disabled={isFromSplit}
            mandatory
          />
        </div>
      )}
      <TextInput
        type="number"
        step="0.01"
        name={`${prefix}montant`}
        placeholder="535,43"
        disabled={isFromTransac || isFromSplit}
        title={isFromTransac || isFromSplit ? disabledFieldHint : undefined}
        label={`Montant ${
          operationType === OperationType.SPLIT ? "total" : ""
        }`}
        mandatory
        min={null}
        unit={"€"}
        marginForm={""}
      />
      <BalanceAmountDetailsFields
        prefix={prefix}
        balanceType={selectedType}
        disabled={isFromSplit}
      />
      {!isFromTransac && !isFromSplit && !isPaiementType && (
        <div className={styles.tva}>
          <div className={styles.tvaToggle}>
            <CheckboxStandalone
              checked={isTvaEnabled}
              onChange={(e) =>
                setFieldValue(tauxTvaFieldName, e.target.checked ? 20 : 0)
              }
            >
              Soumis à la TVA
            </CheckboxStandalone>
            {isTvaEnabled && (
              <ReactSelect
                options={TVA_RATE_OPTIONS}
                name={tauxTvaFieldName}
                placeholder="20%"
                isClearable={false}
              />
            )}
          </div>
          {isTvaEnabled && montant !== 0 && (
            <Text variant="deci">
              Montant HT : {currencyFormatter(montantHT)} | TVA :{" "}
              {currencyFormatter(montantTVA)}
            </Text>
          )}
        </div>
      )}
      {showRemboursementCheckbox && !isFromTransac && !isFromSplit && (
        <Checkbox name={`${prefix}is_remboursement`}>
          Il s'agit d'un remboursement{" "}
          <Tooltip
            content={
              "Permet de forcer un montant positif plutôt que négatif habituellement pour ce type de balance."
            }
          >
            <Interrogation />
          </Tooltip>
        </Checkbox>
      )}
      <TextInput
        type="text"
        name={`${prefix}comment`}
        disabled={isFromTransac || isFromSplit}
        title={isFromTransac || isFromSplit ? disabledFieldHint : undefined}
        placeholder="Virement de M.DUPONT"
        label={"Commentaire"}
        marginForm={""}
      />
      {/*Il faut l'afficher que si on ajoute un type récupérable,
      depuis un folder, pas si on édite. Sinon ca va tout écraser ce qui aurai
      pu être fait en régul de charge */}
      {isRecuperable &&
        !(isEdit && domaineBalance === BalanceDomain.FOLDER) && (
          <RefacturationsFieldArray
            name={`${prefix}refacturation_values`}
            propertyId={propertyId}
            disabled={isFromTransac || isFromSplit}
          />
        )}

      {operationType === OperationType.SPLIT &&
        splitDomaine &&
        splitParentId && (
          <BalanceSplitFields
            name={splitInputsName}
            parentId={splitParentId}
            domaineBalance={splitDomaine}
            totalAmountCents={totalAmountCents}
            childBalances={childBalances}
            hasPeriod={hasPeriod}
            periodeDebut={
              values[`${prefix}periode_debut`] as string | undefined
            }
            periodeFin={values[`${prefix}periode_fin`] as string | undefined}
          />
        )}

      {withJustificatif && (
        <FileInput
          documentId={balanceId ?? undefined}
          doctype={DocType.JUSTIFICATIF_BALANCE}
          path={`${prefix}files`}
          disabled={isFromSplit}
          label={"Justificatifs : "}
        />
      )}
    </>
  );
}
